import React, { useState } from "react";

import { Plus } from "lucide-react";

import {
  Checkbox,
  EditableRow,
  Field,
  inputClass,
  OriginPill,
  Panel,
  Pill,
  RowActions,
  smallButton,
} from "./parts";
import { S } from "./strings";
import { isCustomisedFromDefault, itemOrigin } from "./templateDiff";
import { useAppDispatch } from "../../Store/Hooks";
import {
  addTemplateSection,
  canMoveSection,
  moveTemplateItem,
  PlanTemplate,
  removeTemplateItem,
  resetTemplateItem,
  SectionOwner,
  TemplateSection,
  updateTemplateSection,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

interface Props {
  template: PlanTemplate;
  defaults: PlanTemplate;
  isDefaultScope: boolean;
  liveKeys: Set<string>;
  owners: SectionOwner[];
}

const summary = (s: TemplateSection): string => {
  if (s.system) return S.systemFacts;
  return [
    s.custom ? S.customSection : "",
    s.owner ? S.ownerMeta(s.owner) : "",
    s.max_items ? S.upTo(s.max_items) : "",
    s.collapsed_for_csm ? S.collapsedCsm : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

const SectionsPanel: React.FC<Props> = ({
  template,
  defaults,
  isDefaultScope,
  liveKeys,
  owners,
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<string | null>(null);

  const add = () => {
    const key = `custom-s-${Date.now()}`;
    dispatch(addTemplateSection({ key }));
    setOpen(key);
  };

  return (
    <Panel
      title={S.sectionsTitle}
      hint={isDefaultScope ? S.sectionsHint : S.sectionsHintTenant}
    >
      <ol className="m-0 list-none p-0">
        {template.sections.map((s, i) => {
          const update = (patch: Partial<TemplateSection>) =>
            dispatch(updateTemplateSection({ key: s.key, patch }));
          // The standard sections are the ExecBridge standard: a tenant can't rename them.
          const titleLocked = !s.custom && !isDefaultScope;
          return (
            <EditableRow
              key={s.key}
              index={i + 1}
              title={s.title || S.untitledSection}
              dim={!s.enabled}
              sub={summary(s)}
              open={open === s.key}
              onToggle={() => setOpen(open === s.key ? null : s.key)}
              tags={
                <>
                  {!s.enabled && <Pill tone="faint">{S.hidden}</Pill>}
                  {!liveKeys.has(s.key) && isDefaultScope && (
                    <Pill tone="amber">{S.newItem}</Pill>
                  )}
                  {s.custom && <Pill tone="blue">{S.custom}</Pill>}
                  <OriginPill
                    origin={itemOrigin(isDefaultScope, defaults, "sections", s)}
                  />
                </>
              }
            >
              <Field
                label={S.titleLabel}
                help={titleLocked ? S.standardTitleLocked : undefined}
                htmlFor={`s-title-${s.key}`}
              >
                <input
                  id={`s-title-${s.key}`}
                  type="text"
                  value={s.title}
                  readOnly={titleLocked}
                  aria-readonly={titleLocked}
                  onChange={e => update({ title: e.target.value })}
                  className={
                    titleLocked
                      ? inputClass
                          .replace("bg-white", "bg-gray-100")
                          .replace("text-gray-900", "text-gray-500") +
                        " cursor-not-allowed"
                      : inputClass
                  }
                />
              </Field>
              {s.custom ? (
                <Field
                  label={S.instructionsLabel}
                  help={S.instructionsHelp}
                  htmlFor={`s-instr-${s.key}`}
                >
                  <textarea
                    id={`s-instr-${s.key}`}
                    rows={3}
                    value={s.instructions ?? ""}
                    placeholder={S.instructionsPlaceholder}
                    onChange={e => update({ instructions: e.target.value })}
                    className={inputClass}
                  />
                </Field>
              ) : (
                <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  {s.about}{" "}
                  {isDefaultScope ? S.builtinNote : S.builtinNoteTenant}
                </p>
              )}
              {!s.system && (
                <div className="flex flex-wrap items-end gap-4">
                  <Field label={S.ownerLabel} htmlFor={`s-owner-${s.key}`}>
                    <select
                      id={`s-owner-${s.key}`}
                      value={s.owner ?? ""}
                      onChange={e =>
                        update({ owner: e.target.value as SectionOwner })
                      }
                      className={`${inputClass} w-28`}
                    >
                      {owners.map(o => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {s.max_items !== null && (
                    <Field label={S.maxItemsLabel} htmlFor={`s-max-${s.key}`}>
                      <input
                        id={`s-max-${s.key}`}
                        type="number"
                        min={1}
                        max={40}
                        value={s.max_items}
                        onChange={e =>
                          update({
                            max_items: Math.min(
                              40,
                              Math.max(1, Number(e.target.value) || 1)
                            ),
                          })
                        }
                        className={`${inputClass} w-24`}
                      />
                    </Field>
                  )}
                  <div className="pb-1.5">
                    <Checkbox
                      id={`s-csm-${s.key}`}
                      label={S.collapsedLabel}
                      checked={s.collapsed_for_csm}
                      onChange={collapsed_for_csm =>
                        update({ collapsed_for_csm })
                      }
                    />
                  </div>
                </div>
              )}
              <RowActions
                canUp={canMoveSection(
                  template.sections,
                  s.key,
                  -1,
                  isDefaultScope
                )}
                canDown={canMoveSection(
                  template.sections,
                  s.key,
                  1,
                  isDefaultScope
                )}
                onMove={by =>
                  dispatch(
                    moveTemplateItem({ kind: "sections", key: s.key, by })
                  )
                }
                onReset={
                  !isDefaultScope &&
                  isCustomisedFromDefault(defaults, "sections", s)
                    ? () =>
                        dispatch(
                          resetTemplateItem({ kind: "sections", key: s.key })
                        )
                    : undefined
                }
                onRemove={
                  s.custom
                    ? () => {
                        dispatch(
                          removeTemplateItem({ kind: "sections", key: s.key })
                        );
                        setOpen(null);
                      }
                    : undefined
                }
              >
                {!s.system && (
                  <Checkbox
                    id={`s-on-${s.key}`}
                    label={S.showInBriefing}
                    checked={s.enabled}
                    onChange={enabled => update({ enabled })}
                  />
                )}
              </RowActions>
            </EditableRow>
          );
        })}
      </ol>
      <div className="border-t border-dashed border-gray-300 px-4 py-3">
        <button type="button" className={smallButton} onClick={add}>
          <span className="inline-flex items-center gap-1">
            <Plus size={14} />
            {S.addSection}
          </span>
        </button>
      </div>
    </Panel>
  );
};

export default SectionsPanel;
